/**
 * useSliderList – custom hook to manage the slider list data and functions.
 */
import AdUtils from '@shared/utils/ads';
import Direction, { ArrowDirection } from '@shared/types/enums/direction';
import DotsContainer from '@components/atoms/container/sliderDotsContainer';
import Focus from '@shared/types/enums/focus';
import Main from '@shared/types/enums/main';
import MainContext from '@shared/components/utils/context/mainContext';
import Slider from '@shared/classes/slider';
import SliderImage from '@components/atoms/images/sliderImage';
import goToContent from '@shared/actions/content/goToContent';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { SLIDER_FOCUS_BLACKLIST } from '@shared/types/constants/focusVisibility';
import {
  routerSelector,
  updateParams,
} from '@shared/store/slices/router.slice';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Constants
 *
 * CLICK_BLOCK_DURATION: The duration to block clicks after a click. (400 milliseconds)
 * TRANSITION_DURATION: The duration of the transition between slides. (0.3 seconds)
 * TRANSITION_INTERVAL: The interval between slides. (8 seconds)
 */
const CLICK_BLOCK_DURATION = 400;
const TRANSITION_DURATION = 0.3;
const TRANSITION_INTERVAL = 8 * 1000;

/**
 * Custom hook to manage the slider list data and functions to set the index from the list.
 * @returns {Object} An object containing the slider list data and functions
 */
export default function useSliderList() {
  // ------------------------------------------------------------
  // Initialization
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { params } = useAppSelector(routerSelector);
  const { rails, sliders, onChangeElement } = useContext(MainContext);

  // State
  const [index, setIndex] = useState(
    (params?.current?.sliderIndex as number) ?? 0
  );

  // Refs
  const blockClickUntilRef = useRef<number>(0); // Prevents multiple clicks from moving the index multiple times.
  const slidersRef = useRef(sliders);
  const indexRef = useRef(index);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // The interval for the auto advance.
  indexRef.current = index;
  slidersRef.current = sliders;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  // Pauses the auto advance when the key is pressed.
  const pauseAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Starts the auto advance when the display is ready.
  const startAutoAdvance = useCallback(() => {
    if (!slidersRef.current?.length) return;
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      const { focus } = store.getState();
      if (SLIDER_FOCUS_BLACKLIST.includes(focus.focus as number)) return;
      setIndex((prev) =>
        slidersRef.current?.length
          ? (prev + 1) % slidersRef.current.length
          : prev
      );
    }, TRANSITION_INTERVAL);
  }, []);

  // Resets the auto advance interval.
  const resetAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startAutoAdvance();
  }, [startAutoAdvance]);

  // Handles the change event on the slider list. Dedupes impressions for the same index within a short window (e.g. double report when cycling last→first).
  const onChange = useCallback((newIndex: number) => {
    if (slidersRef.current) {
      slidersRef.current[newIndex]?.impressions?.bannerFocus?.forEach(
        (impression) => {
          AdUtils.sendImageImpression(impression);
        }
      );
    }
    setIndex(newIndex);
    dispatch(updateParams({ sliderIndex: newIndex }));
  }, []);

  // Handles the enter event on the slider list.
  const onEnter = useCallback(() => {
    dispatch(updateParams({ fromSlider: true, sliderIndex: indexRef.current }));
    goToContent(slidersRef.current?.[indexRef.current]);
  }, []);

  // Handles the click event on the slider arrows.
  // Throttled by TRANSITION_DURATION so we don't process presses faster than the animation.
  const onArrowClick = useCallback((direction: ArrowDirection) => {
    const len = slidersRef.current?.length ?? 0;
    if (len === 0) return;
    const now = Date.now();
    if (now - blockClickUntilRef.current < CLICK_BLOCK_DURATION) return;
    blockClickUntilRef.current = now;
    resetAutoAdvance();
    setIndex((prev) =>
      direction === ArrowDirection.LEFT
        ? (prev - 1 + len) % len
        : (prev + 1) % len
    );
  }, []);

  // Handles the click event on the slider controls.
  const onControlsClick = useCallback(() => {
    const item = slidersRef.current?.[indexRef.current];
    if (item) {
      dispatch(
        updateParams({ fromSlider: true, sliderIndex: indexRef.current })
      );
      goToContent(item);
    }
  }, []);

  // Handles the mouse enter event on the slider controls.
  const onControlsEnter = useCallback(() => {
    dispatch(setFocus(Focus.SLIDERS));
  }, []);

  // Renders the slider item.
  const renderItem = useCallback(
    (item: Slider, idx: number) => (
      <SliderImage key={idx} src={item?.sliderImg} />
    ),
    [index, goToContent]
  );

  // ------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------

  // Sets the focus to the slider list when the slider index is set.
  useEffect(() => {
    if (params?.current?.sliderIndex !== undefined) {
      dispatch(setFocus(Focus.SLIDERS));
    }
  }, []);

  // Starts the auto advance when the display is ready.
  useEffect(() => {
    if (!sliders?.length) return;
    dispatch(
      updateParams({
        hasSliders: true,
      })
    );
    startAutoAdvance();
    return () => {
      pauseAutoAdvance();
    };
  }, [sliders]);

  // ------------------------------------------------------------
  // Keyboard
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.SLIDERS,
    keyDownHandler: () => null,
    keyUpHandler: (key) => {
      if (key === 'DOWN' && rails) {
        dispatch(
          setFocus([Focus.RAILS_LIST, parseInt(Focus.RAILS_SUB_LIST + '0')])
        );
        onChangeElement(Main.RAILS);
      }
    },
  });

  // ------------------------------------------------------------
  // Memoization
  // ------------------------------------------------------------

  // Returns the props and callbacks for the slider list.
  const data = useMemo(() => {
    const length = sliders?.length ?? 0;
    return {
      props: {
        paginationDots: {
          Container: DotsContainer,
          index,
          length,
        },
        list: {
          direction: Direction.HORIZONTAL,
          duration: TRANSITION_DURATION,
          focus: Focus.SLIDERS,
          initialIndex: index,
          items: sliders ?? [],
          offset: 1920,
        },
      },
      callbacks: {
        arrows: {
          onClick: onArrowClick,
        },
        controls: {
          onClick: onControlsClick,
          onMouseEnter: onControlsEnter,
        },
        list: {
          onChange,
          onKeyDown: pauseAutoAdvance,
          onKeyUp: startAutoAdvance,
          onEnter,
          renderItem,
        },
      },
    };
  }, [
    index,
    sliders,
    onArrowClick,
    onControlsClick,
    onControlsEnter,
    onChange,
    onEnter,
    pauseAutoAdvance,
    startAutoAdvance,
    renderItem,
  ]);

  // Return slider list required data including props and callbacks
  return data;
}
